import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeader, Loading } from '../../components/office';
import { purchaseOrdersApi, stockApi, suppliersApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@verlo/shared/components/ui/dialog';
import { useAuthoringSite } from '../../contexts/SiteContext';
import { qty, theirs } from '../../lib/orders';

// Bundles (Martin, 27 Oct: "where are my bundles kept?"). A bundle is a basket you order often
// from one supplier. They were only made from an order ("Save as a bundle") and shown on Orders;
// here each can be renamed, have its quantities changed (down to nothing takes a line off),
// have that supplier's products added, be ordered in a tap, or be deleted (with Undo). A new
// one can be started from nothing. Every change saves as it's made.

const money = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v || 0);

export default function BundlesPage() {
  const navigate = useNavigate();
  const authoring = useAuthoringSite();
  const [bundles, setBundles] = useState(null);
  const [stock, setStock] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [making, setMaking] = useState(null);   // { name, supplier_id } while starting a new one
  const [fresh, setFresh] = useState(null);     // the bundle just made, shown first
  const saves = useRef({});

  const load = () => Promise.all([
    purchaseOrdersApi.bundles(), stockApi.getAll(), suppliersApi.getAll().catch(() => ({ data: [] })),
  ]).then(([b, s, sup]) => {
    setBundles(b.data || []);
    setStock((s.data || []).filter((x) => !authoring.site || !x.site_id || x.site_id === authoring.site));
    setSuppliers((sup.data || []).filter((x) => x.status !== 'inactive'));
  }).catch(() => toast.error("Couldn't load the bundles"));
  useEffect(() => { load(); }, [authoring.site]); // eslint-disable-line react-hooks/exhaustive-deps

  const byId = useMemo(() => Object.fromEntries(stock.map((s) => [s.id, s])), [stock]);
  if (!bundles) return <Loading />;
  const shown = fresh ? [...bundles.filter((b) => b.id === fresh), ...bundles.filter((b) => b.id !== fresh)] : bundles;

  const put = (b, patch) => {
    setBundles((all) => all.map((x) => (x.id === b.id ? { ...x, ...patch } : x)));
    const body = { ...patch, ...(patch.lines ? { lines: patch.lines.map((l) => ({ stock_id: l.stock_id, quantity: l.quantity })) } : {}) };
    saves.current[b.id] = (saves.current[b.id] || Promise.resolve())
      .then(() => purchaseOrdersApi.editBundle(b.id, body))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Couldn't save the change"); load(); });
  };
  const step = (b, l, dir) => {
    const pack = Number(byId[l.stock_id]?.pack_size) || 1;
    const next = Math.round((l.quantity + dir * pack) * 1000) / 1000;
    put(b, { lines: next > 0 ? b.lines.map((x) => (x.stock_id === l.stock_id ? { ...x, quantity: next } : x)) : b.lines.filter((x) => x.stock_id !== l.stock_id) });
  };
  const addLine = (b, sid) => {
    const s = byId[sid];
    if (s) put(b, { lines: [...b.lines, { stock_id: s.id, name: s.name, quantity: Number(s.pack_size) || 1 }] });
  };
  const rename = (b, name) => { if (name.trim() && name.trim() !== b.name) put(b, { name: name.trim() }); };

  const create = async () => {
    try {
      const { data } = await purchaseOrdersApi.createBundle({ name: making.name.trim(), supplier_id: making.supplier_id, lines: [] }, authoring.site || undefined);
      setBundles((all) => [data, ...all]);
      setFresh(data.id);
      setMaking(null);
      toast.success(`${data.name} made: add ${theirs(data.supplier_name)} products to it below`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't make the bundle"); }
  };
  // Delete, with Undo: it's made again as it was.
  const remove = async (b) => {
    try {
      await purchaseOrdersApi.removeBundle(b.id);
      setBundles((all) => all.filter((x) => x.id !== b.id));
      toast(`${b.name} deleted`, { action: { label: 'Undo', onClick: async () => {
        try {
          await purchaseOrdersApi.createBundle({ name: b.name, supplier_id: b.supplier_id, lines: b.lines.map((l) => ({ stock_id: l.stock_id, quantity: l.quantity })) });
          load();
        } catch { toast.error("Couldn't put it back"); }
      } } });
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete it"); }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4" data-testid="bundles-page">
      <Link to="/back-office/orders" className="text-[13px] text-ink-soft hover:text-ink">‹ Orders</Link>
      <PageHeader title="Bundles" purpose="Baskets you order often, one supplier each. Order one in a tap; change it here."
        actions={<Button className="bg-verlo hover:bg-verlo-hover" onClick={() => setMaking({ name: '', supplier_id: '' })} disabled={!authoring.ready} data-testid="bundle-new">New bundle</Button>} />

      {!bundles.length && (
        <Notice tone="info" word="Bundles">None yet. Start one here, or save any order as a bundle with “Save as a bundle”.</Notice>
      )}

      {shown.map((b) => {
        const mine = stock.filter((s) => s.supplier_id === b.supplier_id && !b.lines.some((l) => l.stock_id === s.id));
        const cost = b.lines.reduce((t, l) => t + l.quantity * (Number(byId[l.stock_id]?.cost_per_unit) || 0), 0);
        return (
          <section key={b.id} className={`rounded-lg border bg-white ${b.id === fresh ? 'border-verlo' : 'border-hairline'}`} data-testid={`bundle-card-${b.id}`}>
            <div className="flex flex-wrap items-center gap-3 border-b border-hairline px-4 py-3">
              <Input defaultValue={b.name} maxLength={80} onBlur={(e) => rename(b, e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                aria-label="Bundle name" className="h-9 w-64 font-heading font-bold" data-testid={`bundle-name-${b.id}`} />
              <span className="text-[13px] text-ink-soft">{b.supplier_name} · {b.lines.length} item{b.lines.length === 1 ? '' : 's'} · about <span className="font-mono text-ink">{money(cost)}</span></span>
              <div className="ml-auto flex items-center gap-3">
                <button type="button" className="text-[13px] text-ink-faint hover:text-signal-stop" onClick={() => remove(b)} data-testid={`bundle-delete-${b.id}`}>Delete</button>
                <Button size="sm" className="bg-verlo hover:bg-verlo-hover" disabled={!b.lines.length} onClick={() => navigate(`/back-office/orders/new?bundle=${b.id}`)} data-testid={`bundle-order-${b.id}`}>Order this</Button>
              </div>
            </div>
            {b.lines.length ? (
              <ul className="divide-y divide-hairline">
                {b.lines.map((l) => {
                  const item = byId[l.stock_id];
                  return (
                    <li key={l.stock_id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-2" data-testid={`bundle-line-${l.stock_id}`}>
                      <span className="min-w-0 truncate text-[14px] text-ink">{l.name}{!item && <span className="ml-2 text-[12px] text-ink-faint">not in this site&apos;s stock</span>}</span>
                      <span className="flex h-8 items-center overflow-hidden rounded-md border border-hairline-strong bg-white">
                        <button type="button" className="h-full w-7 bg-hover-tint font-bold text-verlo" onClick={() => step(b, l, -1)} aria-label={`Less ${l.name}`}>−</button>
                        <span className="min-w-[72px] px-1 text-center font-mono text-[12.5px] tabular-nums">{qty(l.quantity, item?.unit || '')}</span>
                        <button type="button" className="h-full w-7 bg-hover-tint font-bold text-verlo" onClick={() => step(b, l, 1)} aria-label={`More ${l.name}`}>+</button>
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : <p className="px-4 py-3 text-[13px] text-ink-soft">Nothing in it yet.</p>}
            <div className="border-t border-hairline px-4 py-2.5">
              {mine.length ? (
                <Select value="" onValueChange={(sid) => addLine(b, sid)}>
                  <SelectTrigger className="h-9 w-72" data-testid={`bundle-add-${b.id}`}><SelectValue placeholder={`+ Add from ${theirs(b.supplier_name)} products`} /></SelectTrigger>
                  <SelectContent>{mine.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {money(s.cost_per_unit)}/{s.unit}</SelectItem>)}</SelectContent>
                </Select>
              ) : <span className="text-[12.5px] text-ink-faint">Everything {b.supplier_name} supplies is in it. Give a stock item this supplier in Stock levels to add it.</span>}
            </div>
          </section>
        );
      })}

      <Dialog open={!!making} onOpenChange={(o) => { if (!o) setMaking(null); }}>
        <DialogContent className="max-w-sm" data-testid="bundle-new-dialog">
          <DialogHeader><DialogTitle>New bundle</DialogTitle><DialogDescription>One supplier&apos;s products you order together. Add them once it&apos;s made.</DialogDescription></DialogHeader>
          {making && (
            <div className="space-y-3">
              <label className="block space-y-1.5"><span className="text-[12.5px] text-ink-soft">Name</span>
                <Input value={making.name} onChange={(e) => setMaking({ ...making, name: e.target.value })} maxLength={80} placeholder="e.g. Hartley's, weekly" data-testid="bundle-new-name" /></label>
              <div className="space-y-1.5"><span className="text-[12.5px] text-ink-soft">Supplier</span>
                <Select value={making.supplier_id} onValueChange={(v) => setMaking({ ...making, supplier_id: v })}>
                  <SelectTrigger className="h-9" data-testid="bundle-new-supplier"><SelectValue placeholder="Pick a supplier" /></SelectTrigger>
                  <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMaking(null)}>Cancel</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" disabled={!making?.name.trim() || !making?.supplier_id} onClick={create} data-testid="bundle-new-make">Make the bundle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
